import { useState } from "react";
import { toast } from "sonner";
import { CreditCard, Copy, ArrowSquareOut } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, money, fmtDateTime } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";

const STATUS = { pending: "Bekliyor", paid: "Ödendi", expired: "Süresi doldu", failed: "Başarısız" };
const PROVIDERS = [["stripe", "Stripe"], ["paystack", "Paystack"]];

export default function PayLinkDialog({ open, onOpenChange, invoice, onPaid }) {
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState("stripe");
  const [providers] = useLoad(() => (open ? api.get("/pay/providers").then((r) => r.data) : Promise.resolve(null)), [open]);
  const [links, reload] = useLoad(() => (open ? api.get(`/invoices/${invoice.id}/pay-links`).then((r) => r.data) : Promise.resolve([])), [open, invoice.id]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/invoices/${invoice.id}/pay-link`, { origin_url: window.location.origin, provider });
      toast.success("Ödeme bağlantısı hazır");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const copy = (url) => navigator.clipboard.writeText(url).then(() => toast.success("Bağlantı kopyalandı"));
  const check = async (l) => {
    try {
      const { data } = await api.get(`/pay/status/${l.session_id}`);
      reload();
      if (data.payment_status === "paid") { toast.success("Ödeme alındı, tahsilat işlendi"); onPaid(); }
      else toast.info(`Durum: ${STATUS[data.payment_status] || data.payment_status}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-[4px]" data-testid="paylink-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CreditCard className="text-teal-700" /> Online Ödeme Bağlantısı</DialogTitle>
          <DialogDescription>Müşteriniz {invoice.no} faturasının kalan tutarını kartla öder; ödeme cariye otomatik tahsilat olarak işlenir.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2" data-testid="paylink-provider-group">
          {PROVIDERS.map(([key, label]) => (
            <button key={key} type="button" onClick={() => setProvider(key)} data-testid={`paylink-provider-${key}`}
              className={`flex-1 border px-3 py-2 text-sm font-semibold transition-colors ${provider === key ? "border-teal-700 bg-teal-50 text-teal-800" : "border-border text-muted-foreground hover:bg-muted"}`}>
              {label}{providers && !providers[key] && <span className="ml-1 text-[10px] font-normal text-orange-600">(anahtar yok)</span>}
            </button>
          ))}
        </div>
        {provider === "stripe" && <div className="border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800" data-testid="paylink-test-mode-note">
          TEST MODU: Gerçek para çekilmez. Deneme kartı 4242 4242 4242 4242, ileri bir tarih ve herhangi bir CVC.
        </div>}
        {provider === "paystack" && <div className="border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="paylink-paystack-note">
          Paystack ödemesi için carinin e-posta adresi gerekir; ödeme tamamlanınca tahsilat otomatik işlenir.
        </div>}
        <Button onClick={create} disabled={busy} className="rounded-[4px] bg-teal-700 hover:bg-teal-800" data-testid="paylink-create-button">
          <CreditCard /> {busy ? "Oluşturuluyor…" : "Yeni Ödeme Bağlantısı Oluştur"}
        </Button>
        <ul className="max-h-64 divide-y divide-border overflow-y-auto border border-border text-sm" data-testid="paylink-list">
          {links && links.length === 0 && <li className="p-4 text-center text-muted-foreground">Henüz bağlantı oluşturulmadı.</li>}
          {links?.map((l) => (
            <li key={l.session_id} className="flex items-center gap-2 p-3" data-testid={`paylink-row-${l.session_id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-num font-semibold">{money(l.amount)}</div>
                <div className="text-[11px] text-muted-foreground">{fmtDateTime(l.created_at)} · <span className="uppercase">{l.provider || "stripe"}</span> · <span className={l.payment_status === "paid" ? "font-semibold text-teal-700" : ""}>{STATUS[l.payment_status] || l.payment_status}</span>{l.payment_no && ` · ${l.payment_no}`}</div>
              </div>
              {l.payment_status === "pending" && (<>
                <Button size="icon" variant="ghost" onClick={() => copy(l.url)} data-testid={`paylink-copy-${l.session_id}`}><Copy /></Button>
                <Button size="icon" variant="ghost" onClick={() => window.open(l.url, "_blank")} data-testid={`paylink-open-${l.session_id}`}><ArrowSquareOut /></Button>
                <Button size="sm" variant="outline" onClick={() => check(l)} data-testid={`paylink-check-${l.session_id}`}>Kontrol Et</Button>
              </>)}
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
